// File: src/pages/PartyLedgersView.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
} from "reactstrap";
import Multiselect from "multiselect-react-dropdown";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { ImDownload } from "react-icons/im";
import { AiOutlineFilePdf } from "react-icons/ai";
import swal from "sweetalert";
import { pdf } from "@react-pdf/renderer";

// ---- your shared imports (as you requested under ../import/*) ----
import SuperAdminUI from "../import/SuperAdminUI";
import {
  CreateCustomerList,
  _Get,
  _GetById,
  _GetById3,
} from "../import/ApiEndPoint/ApiCalling";
import {
  Create_Account_List,
  Create_Transporter_List,
  View_Expense_Account,
  View_Ledger_by_id_database,
  Last_Ledger_Balance,
  View_Invoice_Data_by_id,
  View_Receipt_By_Id,
} from "../import/ApiEndPoint/Api";
import { HsnSummaryCalculation } from "../import/HsnSummaryCalculation";
import LastestInvoice from "./LastestInvoice";
import { baseURL } from "../import/ApiEndPoint/TestApi";

// RUPIO SOFT logo (for "THIS LEDGER IS GENERATED IN ... SOFT")
import RupioSoftLogo from "../import/logo.png";

// ---- tiny CSS injected ONCE (page scoped) ----
const injectOnce = () => {
  if (document.getElementById("ledger-inline-css")) return;
  const style = document.createElement("style");
  style.id = "ledger-inline-css";
  style.innerHTML = `
    .wh-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .wh-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .wh-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:780px; }
    .wh-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .wh-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .wh-table tbody tr:hover{ background:#f9fbff; }
    .pl-card{ border:1px solid #e5e7eb; border-radius:14px; }
    .no-print{ }
    .print-only{ display:none; }

    /* Make "Particular" wide and "Voucher No" narrow (compressed layout) */
    .wh-table thead th:nth-child(2),
    .wh-table tbody td:nth-child(2){
      width:40%;
    }
    .wh-table thead th:nth-child(4),
    .wh-table tbody td:nth-child(4){
      width:8%;
      white-space:nowrap;
    }

    @media print {
      .no-print{ display:none !important; }
      .print-only{ display:block !important; }
      table { page-break-inside:auto; }
      tr { page-break-inside:avoid; page-break-after:auto; }
      thead { display:table-header-group; }
      tfoot { display:table-footer-group; }
    }
  `;
  document.head.appendChild(style);
};

// helpers
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const fmtDate = (s) => (s ? String(s).split("T")[0] : "");
const safeNum = (x) => (isNaN(Number(x)) ? 0 : Number(x));

const normalizeFinancialYear = (value) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})\s*-\s*(\d{2}|\d{4})$/);

  if (!match) return raw;

  const startYear = match[1];
  const endYear = match[2].length === 4 ? match[2].slice(-2) : match[2];

  return `${startYear}-${endYear}`;
};

const getCurrentFinancialYearLabel = (date = new Date()) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const startYear = month >= 4 ? year : year - 1;
  const endYearShort = String(startYear + 1).slice(-2);

  return `${startYear}-${endYearShort}`;
};

const getActiveFinancialYear = () =>
  normalizeFinancialYear(
    localStorage.getItem("financialYearSelected") ||
      getCurrentFinancialYearLabel(),
  );

const getPreviousFinancialYear = (fy) => {
  const normalized = normalizeFinancialYear(fy || getActiveFinancialYear());
  const start = Number(String(normalized || "").split("-")[0]);

  if (!Number.isFinite(start)) return "";

  return `${start - 1}-${String(start).slice(-2)}`;
};

const stripFinancialYearSuffix = (db) =>
  String(db || "").replace(/-\d{4}-\d{2}$/, "");

const buildDatabaseForFY = (db, fy) => {
  const mainDb = stripFinancialYearSuffix(db);
  const financialYear = normalizeFinancialYear(fy || getActiveFinancialYear());

  return financialYear ? `${mainDb}-${financialYear}` : mainDb;
};

const normalizeOpeningType = (value) => {
  const raw = toStr(value).trim().toLowerCase();

  if (["debit", "dr", "dr.", "d"].includes(raw)) return "debit";
  if (["credit", "cr", "cr.", "c"].includes(raw)) return "credit";

  return "";
};

const getOpeningFinancialYear = (opening) =>
  normalizeFinancialYear(
    opening?.openingFinancialYear ??
      opening?.OpeningFinancialYear ??
      opening?.financialYear ??
      opening?.FinancialYear ??
      "",
  );

const openingToSigned = (opening) => {
  const openingType = normalizeOpeningType(
    opening?.openingType ?? opening?.Type,
  );
  const openingAmt = Math.abs(
    safeNum(opening?.openingBalance ?? opening?.OpeningBalance ?? 0),
  );

  if (openingType === "credit") return -openingAmt;
  if (openingType === "debit") return openingAmt;

  return openingAmt;
};

const signedToOpening = (signedValue) => {
  const signed = safeNum(signedValue);

  return {
    openingBalance: Math.abs(signed),
    openingType: signed > 0 ? "debit" : signed < 0 ? "credit" : "",
  };
};

const getLedgerRowsSignedMovement = (rows = []) => {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => {
    return sum + safeNum(row?.debit) - safeNum(row?.credit);
  }, 0);
};

const getFallbackFinancialYearLabel = (dateValue) => {
  if (!dateValue) return "";

  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = d.getMonth() + 1; // Jan = 1, Apr = 4

  const startYear = month >= 4 ? year : year - 1;
  const endYearShort = String(startYear + 1).slice(-2);

  return `${startYear}-${endYearShort}`;
};

const getRowFinancialYear = (row) => {
  const dateBasedFY = getFallbackFinancialYearLabel(
    row?.date || row?.updatedAt || row?.createdAt,
  );

  return dateBasedFY;
};

const getFYStartYear = (fy) => {
  const start = Number(String(fy || "").split("-")[0]);
  return Number.isFinite(start) ? start : 0;
};

const makeFinancialYearLabel = (startYear) =>
  `${startYear}-${String(startYear + 1).slice(-2)}`;

const getFinancialYearsForCarryForward = (openingFY, targetFY) => {
  const openingStart = getFYStartYear(openingFY);
  const targetStart = getFYStartYear(targetFY);

  if (!openingStart || !targetStart || targetStart <= openingStart) return [];

  const years = [];

  // To calculate the opening of targetFY, add movements from the opening FY
  // through the FY immediately before targetFY.
  for (let year = openingStart; year < targetStart; year += 1) {
    years.push(makeFinancialYearLabel(year));
  }

  return years;
};

const normalizeFinancialYearsList = (value) => {
  let list = value;

  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      list = list
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
    }
  }

  if (!Array.isArray(list)) return [];

  return [
    ...new Set(list.map((x) => normalizeFinancialYear(x)).filter(Boolean)),
  ];
};

const sortFinancialYears = (list = []) =>
  [...list].sort((a, b) => getFYStartYear(a) - getFYStartYear(b));

const getKnownFinancialYears = (activeFY, extra = []) => {
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const companyDetail = JSON.parse(
    localStorage.getItem("Companydetail") || "{}",
  );

  const years = [
    ...normalizeFinancialYearsList(user?.financialYears),
    ...normalizeFinancialYearsList(companyDetail?.financialYears),
    ...extra.map((x) => normalizeFinancialYear(x)).filter(Boolean),
    normalizeFinancialYear(activeFY),
  ].filter(Boolean);

  return sortFinancialYears([...new Set(years)]);
};

const getFinancialYearsBefore = (years = [], activeFY = "") => {
  const activeStart = getFYStartYear(activeFY);

  return sortFinancialYears(
    (years || []).filter((fy) => {
      const start = getFYStartYear(fy);
      return start > 0 && activeStart > 0 && start < activeStart;
    }),
  );
};

const filterLedgerByFYAndDate = (list = [], fy = "", filter = {}) => {
  let out = Array.isArray(list) ? [...list] : [];

  if (fy) {
    out = out.filter((row) => getRowFinancialYear(row) === fy);
  }

  if (filter?.startDate && filter?.EndDate) {
    out = out.filter((item) => {
      const src = item?.date || item?.updatedAt;
      const d = src ? new Date(src) : null;
      if (!d || isNaN(d.getTime())) return false;
      const only = d.toISOString().split("T")[0];
      return only >= filter.startDate && only <= filter.EndDate;
    });
  }

  return out;
};

const extractReceiptRemark = (res) => {
  const pickRemark = (obj) =>
    toStr(
      obj?.remark ||
        obj?.remarks ||
        obj?.Remark ||
        obj?.Remarks ||
        obj?.narration ||
        obj?.description ||
        obj?.note,
    ).trim();

  // ✅ your API shape: { Receipts: { ... remark: "..." }, status: true }
  const fromReceipts = pickRemark(res?.Receipts);
  if (fromReceipts) return fromReceipts;

  const direct = pickRemark(res);
  if (direct) return direct;

  const candidates = [
    res?.Receipts,
    res?.Receipt,
    res?.receipt,
    res?.Data,
    res?.data,
    res?.result,
    res?.Result,
    res?.printData,
  ];

  for (const c of candidates) {
    if (!c) continue;
    if (Array.isArray(c)) {
      for (const item of c) {
        const r = pickRemark(item);
        if (r) return r;
      }
    } else {
      const r = pickRemark(c);
      if (r) return r;
    }
  }

  return "";
};

const getLedgerParticular = (row, partyName = "") => {
  const voucherType = toStr(row?.voucherType).toLowerCase();
  const isReceiptOrPayment =
    voucherType.includes("receipt") || voucherType.includes("payment");

  const apiRemark = toStr(row?._receiptRemark).trim();

  if (isReceiptOrPayment && apiRemark) {
    return partyName ? `${partyName} - ${apiRemark}` : apiRemark;
  }

  return toStr(partyName);
};

// convert image URL -> DataURL (for jsPDF)
const loadImageAsDataUrl = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(c.toDataURL("image/png"));
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

// build full image URL from filename (company logo)
const imgURL = (filename) => {
  if (!filename) return "";
  return `${baseURL}/Images/${filename}`;
};

export default function PartyLedgersView() {
  const location = useLocation();
  const autoOpenHandledRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // party & ledger
  const [partyList, setPartyList] = useState([]);
  const [selectedParty, setSelectedParty] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [partyName, setPartyName] = useState("");
  const [ledger, setLedger] = useState([]);
  const [allLedger, setAllLedger] = useState([]);

  // financial year
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");

  // opening balance from party master
  const [openingBalance, setOpeningBalance] = useState({
    openingBalance: 0,
    openingType: "",
    openingFinancialYear: "",
  });

  const [computedOpeningBalance, setComputedOpeningBalance] = useState(null);

  // filters
  const [filter, setFilter] = useState({ startDate: "", EndDate: "" });

  // super admin
  const [isMaster, setIsMaster] = useState(false);

  // company header info for print/pdf
  const companyDetail = useMemo(
    () => JSON.parse(localStorage.getItem("Companydetail") || "{}"),
    [],
  );

  // PDF preview modal
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfOpen, setPdfOpen] = useState(false);

  useEffect(() => injectOnce(), []);

  const getCurrentScope = () => {
    const userdata = JSON.parse(localStorage.getItem("userData") || "{}");
    const role = userdata?.rolename?.roleName;

    let db = userdata?.database || "";
    let uid = userdata?._id || "";

    if (role === "MASTER") {
      const raw = localStorage.getItem("SuperadminIdByMaster");

      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          const text = String(parsed || "");
          const parts = text.split(" ");

          if (parts[0]) uid = parts[0];
          if (parts[1]) db = parts[1];
        } catch {
          const text = String(raw || "").replace(/^"|"$/g, "");
          const parts = text.split(" ");

          if (parts[0]) uid = parts[0];
          if (parts[1]) db = parts[1];
        }
      }
    }

    return { userdata, role, uid, db };
  };

  const selectedYearOpening = useMemo(() => {
    return computedOpeningBalance || openingBalance;
  }, [computedOpeningBalance, openingBalance]);

  // initial lists
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const userdata = JSON.parse(localStorage.getItem("userData") || "{}");
        setIsMaster(userdata?.rolename?.roleName === "MASTER");

        const url = `${Create_Account_List}${userdata?._id}/`;
        const [custRes, accRes, expRes, trRes] = await Promise.allSettled([
          CreateCustomerList(userdata?._id, userdata?.database),
          _Get(url, userdata?.database),
          _Get(View_Expense_Account, userdata?.database),
          _Get(Create_Transporter_List, userdata?.database),
        ]);

        const transporters = Array.isArray(trRes?.value?.Transporter)
          ? trRes.value.Transporter.map((t) => ({
              ...t,
              transporter: true,
              userType: "transporter",
              fullName: t?.companyName || "-",
              OpeningBalance: t?.OpeningBalance,
              Type: t?.Type,
              openingFinancialYear:
                t?.openingFinancialYear ?? t?.OpeningFinancialYear ?? "",
            }))
          : [];

        const customers = Array.isArray(custRes?.value?.Customer)
          ? custRes.value.Customer.map((c) => ({
              ...c,
              party: true,
              userType: "party",
              fullName: c?.CompanyName || "-",
              OpeningBalance: c?.OpeningBalance,
              Type: c?.Type,
              openingFinancialYear:
                c?.openingFinancialYear ?? c?.OpeningFinancialYear ?? "",
            }))
          : [];

        const users = Array.isArray(accRes?.value?.adminDetails)
          ? accRes.value.adminDetails.map((u) => ({
              ...u,
              User: true,
              userType: "User",
              fullName: u?.firstName || "-",
              OpeningBalance: u?.OpeningBalance,
              Type: u?.Type,
              openingFinancialYear:
                u?.openingFinancialYear ?? u?.OpeningFinancialYear ?? "",
            }))
          : [];

        const expenses = Array.isArray(expRes?.value?.Expenses)
          ? expRes.value.Expenses.map((e) => ({
              ...e,
              miscellaneous: true,
              userType: "miscellaneous",
              fullName: `${e?.title || ""} ${e?.type || ""}`.trim(),
              OpeningBalance: e?.OpeningBalance,
              Type: e?.Type,
              openingFinancialYear:
                e?.openingFinancialYear ?? e?.OpeningFinancialYear ?? "",
            }))
          : [];

        setPartyList([...customers, ...users, ...expenses, ...transporters]);
      } catch (e) {
        console.error(e);
        swal("Failed to load parties");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // super admin switch
  const handleParentSubmit = async (e) => {
    e.preventDefault();
    const picked = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null",
    );
    if (!picked) return;
    try {
      setBusy(true);
      const [id, db] = String(picked).split(" ");
      const url = `${Create_Account_List}${id}/`;
      const [custRes, accRes, expRes, trRes] = await Promise.allSettled([
        CreateCustomerList(id, db),
        _Get(url, db),
        _Get(View_Expense_Account, db),
        _Get(Create_Transporter_List, db),
      ]);
      const transporters = Array.isArray(trRes?.value?.Transporter)
        ? trRes.value.Transporter.map((t) => ({
            ...t,
            transporter: true,
            userType: "transporter",
            fullName: t?.companyName || "-",
            OpeningBalance: t?.OpeningBalance,
            Type: t?.Type,
            openingFinancialYear:
              t?.openingFinancialYear ?? t?.OpeningFinancialYear ?? "",
          }))
        : [];
      const customers = Array.isArray(custRes?.value?.Customer)
        ? custRes.value.Customer.map((c) => ({
            ...c,
            party: true,
            userType: "party",
            fullName: c?.CompanyName || "-",
            OpeningBalance: c?.OpeningBalance,
            Type: c?.Type,
            openingFinancialYear:
              c?.openingFinancialYear ?? c?.OpeningFinancialYear ?? "",
          }))
        : [];
      const users = Array.isArray(accRes?.value?.adminDetails)
        ? accRes.value.adminDetails.map((u) => ({
            ...u,
            User: true,
            userType: "User",
            fullName: u?.firstName || "-",
            OpeningBalance: u?.OpeningBalance,
            Type: u?.Type,
            openingFinancialYear:
              u?.openingFinancialYear ?? u?.OpeningFinancialYear ?? "",
          }))
        : [];
      const expenses = Array.isArray(expRes?.value?.Expenses)
        ? expRes.value.Expenses.map((e) => ({
            ...e,
            miscellaneous: true,
            userType: "miscellaneous",
            fullName: `${e?.title || ""} ${e?.type || ""}`.trim(),
            OpeningBalance: e?.OpeningBalance,
            Type: e?.Type,
            openingFinancialYear:
              e?.openingFinancialYear ?? e?.OpeningFinancialYear ?? "",
          }))
        : [];
      setPartyList([...customers, ...users, ...expenses, ...transporters]);
    } catch (e) {
      console.error(e);
      swal("Failed to load parties for SuperAdmin");
    } finally {
      setBusy(false);
    }
  };

  // party selection → auto load ledger
  const handleSelectionParty = async (selectedList, selectedItem) => {
    // FIRST STEP:
    // Read openingFinancialYear + OpeningBalance + Type from the selected master.
    const selectedOpening = {
      openingBalance: Math.abs(safeNum(selectedItem?.OpeningBalance)),
      openingType: normalizeOpeningType(selectedItem?.Type),
      openingFinancialYear: getOpeningFinancialYear(selectedItem),
    };

    setPartyId(selectedItem?._id || "");
    setPartyName(selectedItem?.fullName || "");
    setSelectedParty(selectedItem ? [selectedItem] : []);
    setOpeningBalance(selectedOpening);
    setComputedOpeningBalance(null);
    setFilter({ startDate: "", EndDate: "" });

    await fetchLedger(selectedItem?._id || "", selectedOpening);
  };

  // Auto-open the exact ledger passed from UserOpeningClosingBalance.
  //
  // IMPORTANT:
  // Do NOT open from the small navigation object immediately. The Ledger page
  // first loads its own master party/user/account/transporter list, then finds
  // the exact full record by ID and sends THAT record through the same
  // handleSelectionParty() path used by a normal dropdown click.
  //
  // This avoids the "correct for one second, then zero" race caused by the
  // page initialization replacing an early/incomplete selection.
  useEffect(() => {
    if (autoOpenHandledRef.current) return;

    const handoff = location?.state?.autoOpenLedger;
    const selectedId = String(handoff?._id || handoff?.id || "").trim();

    if (!selectedId) return;

    // Wait until the normal Ledger master-list request has finished.
    if (loading) return;

    const exactRecord = (Array.isArray(partyList) ? partyList : []).find(
      (item) => String(item?._id || item?.id || "").trim() === selectedId,
    );

    // If the list is still being populated/replaced, do not consume the
    // handoff yet. The effect will run again when partyList changes.
    if (!exactRecord) return;

    autoOpenHandledRef.current = true;

    handleSelectionParty([], exactRecord);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state, loading, partyList]);

  const onRemoveParty = () => {
    setSelectedParty([]);
    setPartyId("");
    setPartyName("");
    setOpeningBalance({
      openingBalance: 0,
      openingType: "",
      openingFinancialYear: "",
    });
    setComputedOpeningBalance(null);
    setFinancialYears([]);
    setSelectedFY("");
    setLedger([]);
    setAllLedger([]);
  };

  // summary
  const computeSummary = (arr) => {
    const obDebit =
      selectedYearOpening.openingType === "debit"
        ? safeNum(selectedYearOpening.openingBalance)
        : 0;
    const obCredit =
      selectedYearOpening.openingType === "credit"
        ? safeNum(selectedYearOpening.openingBalance)
        : 0;

    const ttlDebit =
      arr.reduce((sum, x) => sum + safeNum(x.debit), 0) + obDebit;
    const ttlCredit =
      arr.reduce((sum, x) => sum + safeNum(x.credit), 0) + obCredit;

    const closingDiff = Math.abs(ttlDebit - ttlCredit);

    return {
      ttlDebit,
      ttlCredit,
      closingDiff,
      showOnDebit: ttlCredit > ttlDebit,
      showOnCredit: ttlDebit > ttlCredit,
    };
  };

  const attachReceiptRemarks = async (list = []) => {
    if (!Array.isArray(list) || !list.length) return list;

    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const db = user?.database;

    const orderIds = [
      ...new Set(
        list
          .filter((row) => {
            const vt = toStr(row?.voucherType).toLowerCase();
            return (
              (vt.includes("receipt") || vt.includes("payment")) && row?.orderId
            );
          })
          .map((row) => toStr(row.orderId))
          .filter(Boolean),
      ),
    ];

    if (!orderIds.length) return list;

    const results = await Promise.allSettled(
      orderIds.map(async (orderId) => {
        const res = await _GetById3(View_Receipt_By_Id, orderId);
        return { orderId, remark: extractReceiptRemark(res) };
      }),
    );

    const remarkMap = {};
    results.forEach((r) => {
      if (r.status === "fulfilled") {
        remarkMap[r.value.orderId] = r.value.remark || "";
      }
    });

    return list.map((row) => ({
      ...row,
      _receiptRemark: row?.orderId ? toStr(remarkMap[toStr(row.orderId)]) : "",
    }));
  };

  const fetchLedgerRowsForFY = async (targetId, db, fy) => {
    try {
      const normalizedFY = normalizeFinancialYear(fy);
      const selectedDatabase = buildDatabaseForFY(db, normalizedFY);
      const ledgerUrl = `${View_Ledger_by_id_database}${targetId}/`;
      const res = await _Get(ledgerUrl, selectedDatabase);

      const rawRows = Array.isArray(res?.Ledger) ? res.Ledger : [];

      /*
        IMPORTANT:
        Do NOT assume every row returned from `<database>-<FY>` belongs to
        that FY. Old/migrated data can contain rows from another year.

        The visible ledger itself is date-filtered FY-wise, therefore the
        carry-forward MUST use the exact same rows. Otherwise hidden rows can
        change the previous closing and produce a wrong next-year opening.
      */
      const fyRows = rawRows.filter(
        (row) => getRowFinancialYear(row) === normalizedFY,
      );

      console.log(
        `[PartyLedgersView] FY ${normalizedFY}: ${rawRows.length} raw rows -> ${fyRows.length} valid FY rows`,
      );

      return fyRows;
    } catch (err) {
      console.warn(`[PartyLedgersView] ledger not found for FY ${fy}`, err);
      return [];
    }
  };

  // fetch ledger
  const fetchLedger = async (
    idOverride = "",
    openingOverride = null,
    fyOverride = "",
  ) => {
    const targetId = idOverride || partyId;

    if (!targetId) {
      swal("Please select a party");
      return;
    }

    setBusy(true);

    try {
      const { db } = getCurrentScope();

      // The ledger always opens for financialYearSelected unless an explicit
      // FY is passed by the (currently hidden) FY selector.
      const activeFY = normalizeFinancialYear(
        fyOverride || getActiveFinancialYear(),
      );

      const baseOpeningRaw = openingOverride || openingBalance;

      const baseOpening = {
        openingBalance: Math.abs(
          safeNum(
            baseOpeningRaw?.openingBalance ??
              baseOpeningRaw?.OpeningBalance ??
              0,
          ),
        ),
        openingType: normalizeOpeningType(
          baseOpeningRaw?.openingType ?? baseOpeningRaw?.Type,
        ),
        openingFinancialYear: getOpeningFinancialYear(baseOpeningRaw),
      };

      const openingFY = baseOpening.openingFinancialYear;
      const activeStart = getFYStartYear(activeFY);
      const openingStart = getFYStartYear(openingFY);

      let effectiveOpening = {
        openingBalance: baseOpening.openingBalance,
        openingType: baseOpening.openingType,
      };

      /*
        OPENING BALANCE RULE

        1) If openingFinancialYear === financialYearSelected:
           use OpeningBalance + Type directly from the selected master.

        2) If financialYearSelected is after openingFinancialYear:
           opening of the selected FY = closing of the immediately previous FY.
           To get that correctly, start from the master's opening and carry the
           signed balance through every FY from openingFinancialYear up to the
           FY immediately before financialYearSelected.

           Example:
           openingFinancialYear = 2025-26
           financialYearSelected = 2026-27

           Opening 2026-27 =
             Opening 2025-26
             + Debit movements of 2025-26
             - Credit movements of 2025-26

           That result is exactly the Closing Balance of 2025-26.

        3) If selected FY is before openingFinancialYear:
           there was no opening defined for that earlier year, so opening = 0.
      */
      if (openingFY && activeFY === openingFY) {
        effectiveOpening = {
          openingBalance: baseOpening.openingBalance,
          openingType: baseOpening.openingType,
        };
      } else if (openingFY && openingStart > 0 && activeStart > openingStart) {
        let signedOpening = openingToSigned(baseOpening);

        const carryYears = getFinancialYearsForCarryForward(
          openingFY,
          activeFY,
        );

        for (const fy of carryYears) {
          // fetchLedgerRowsForFY already removes any row whose DATE is not
          // inside this FY, so this is the same movement used by the visible
          // ledger/PDF for that year.
          const rows = await fetchLedgerRowsForFY(targetId, db, fy);
          const movement = getLedgerRowsSignedMovement(rows);

          console.log(
            `[PartyLedgersView] Carry ${fy}: opening=${signedOpening.toFixed(2)}, movement=${movement.toFixed(2)}, closing=${(signedOpening + movement).toFixed(2)}`,
          );

          signedOpening += movement;
        }

        effectiveOpening = signedToOpening(signedOpening);
      } else if (
        openingFY &&
        openingStart > 0 &&
        activeStart > 0 &&
        activeStart < openingStart
      ) {
        effectiveOpening = {
          openingBalance: 0,
          openingType: "",
        };
      } else {
        // Backward-compatible fallback for old masters that do not yet have
        // openingFinancialYear: use their OpeningBalance + Type for active FY.
        effectiveOpening = {
          openingBalance: baseOpening.openingBalance,
          openingType: baseOpening.openingType,
        };
      }

      const currentRows = await fetchLedgerRowsForFY(targetId, db, activeFY);
      const enrichedList = await attachReceiptRemarks(currentRows);

      // Keep FY list for the existing hidden selector / future use.
      // Include the opening FY, active FY and every FY between them.
      const carryYears =
        openingFY && activeStart >= openingStart
          ? getFinancialYearsForCarryForward(openingFY, activeFY)
          : [];

      const knownYears = sortFinancialYears(
        [
          ...getKnownFinancialYears(activeFY, [openingFY]),
          ...carryYears,
          activeFY,
        ].filter(Boolean),
      );

      setComputedOpeningBalance(effectiveOpening);
      setAllLedger(enrichedList);
      setFinancialYears(knownYears.length ? knownYears : [activeFY]);
      setSelectedFY(activeFY);

      setLedger(
        filterLedgerByFYAndDate(enrichedList, activeFY, {
          startDate: "",
          EndDate: "",
        }),
      );
    } catch (e) {
      console.error(e);
      swal("No Data Found");
      setLedger([]);
      setAllLedger([]);
      setFinancialYears([]);
      setSelectedFY("");
      setComputedOpeningBalance(null);
    } finally {
      setBusy(false);
    }
  };

  const handleFinancialYearChange = async (e) => {
    const fy = normalizeFinancialYear(e.target.value);

    if (!fy) return;

    setSelectedFY(fy);
    setFilter({ startDate: "", EndDate: "" });

    if (partyId) {
      await fetchLedger(partyId, openingBalance, fy);
    } else {
      setLedger(filterLedgerByFYAndDate(allLedger, fy, filter));
    }
  };

  // filter dates (kept Apply button)
  const applyDateFilter = () => {
    if (!selectedFY) {
      setLedger([]);
      return;
    }

    setLedger(filterLedgerByFYAndDate(allLedger, selectedFY, filter));
  };

  // build jsPDF (sync).
  // NOW: 1) top line centered ("THIS LEDGER IS GENERATED IN [logo] SOFT")
  //      2) table fully fits page width with compressed layout.
  const buildLedgerPdf = (opts = {}) => {
    const { strapLogoDataUrl = null, mainLogoDataUrl = null } = opts;
    const doc = new jsPDF("p", "mm", "a4");

    const pageWidth = doc.internal.pageSize.getWidth();

    // ===== Top strap line (centered) =====
    const strapY = 8;
    const leftText = "THIS LEDGER IS GENERATED IN";
    const rightText = "SOFT";

    doc.setFontSize(8);

    const leftWidth = doc.getTextWidth(leftText);
    const rightWidth = doc.getTextWidth(rightText);
    const imgW = strapLogoDataUrl ? 14 : 0; // narrow logo
    const imgH = strapLogoDataUrl ? 4 : 0;
    const gap = strapLogoDataUrl ? 1.5 : 0;

    const totalStrapWidth = leftWidth + imgW + gap + rightWidth;
    let startX = (pageWidth - totalStrapWidth) / 2;

    // LEFT TEXT
    doc.text(leftText, startX, strapY);
    startX += leftWidth + 1;

    // LOGO IN MIDDLE
    if (strapLogoDataUrl) {
      doc.addImage(
        strapLogoDataUrl,
        "PNG",
        startX,
        strapY - imgH + 1,
        imgW,
        imgH,
      );
      startX += imgW + gap;
    }

    // RIGHT TEXT
    doc.text(rightText, startX, strapY);

    // ===== Company logo + details =====
    let y = strapY + 6;

    if (mainLogoDataUrl) {
      const mainLogoW = 50;
      const mainLogoH = 18;
      const logoX = (pageWidth - mainLogoW) / 2;
      doc.addImage(mainLogoDataUrl, "PNG", logoX, y, mainLogoW, mainLogoH);
      y += mainLogoH + 2;
    }

    const name = toStr(companyDetail?.name);
    const addr = toStr(companyDetail?.address);
    const email = toStr(companyDetail?.email);
    const mobile = toStr(companyDetail?.mobileNo);
    const title = `${toStr(partyName)} - Ledger${selectedFY ? ` (${selectedFY})` : ""}`;

    if (name) {
      doc.setFontSize(14);
      doc.text(name, pageWidth / 2, y, { align: "center" });
      y += 6;
    }
    doc.setFontSize(10);
    if (addr) {
      doc.text(addr, pageWidth / 2, y, { align: "center" });
      y += 5;
    }
    if (email) {
      doc.text(email, pageWidth / 2, y, { align: "center" });
      y += 5;
    }
    if (mobile) {
      doc.text(mobile, pageWidth / 2, y, { align: "center" });
      y += 5;
    }

    doc.setFontSize(13);
    doc.text(title, pageWidth / 2, y + 6, { align: "center" });

    // ===== Table rows =====
    const body = [];

    // Opening Balance
    const obDebit =
      selectedYearOpening.openingType === "debit"
        ? safeNum(selectedYearOpening.openingBalance).toFixed(2)
        : "";
    const obCredit =
      selectedYearOpening.openingType === "credit"
        ? safeNum(selectedYearOpening.openingBalance).toFixed(2)
        : "";
    body.push(["", "Opening Balance", "", "", obDebit, obCredit]);

    // Ledger rows
    ledger.forEach((x) => {
      const date = fmtDate(x?.date);
      const particular = getLedgerParticular(x, partyName);
      const vt = toStr(x?.voucherType);
      const vn = toStr(x?.invoiceId || x?.voucherNo || "");
      const dr = x?.debit ? safeNum(x.debit).toFixed(2) : "";
      const cr = x?.credit ? safeNum(x.credit).toFixed(2) : "";
      body.push([date, particular, vt, vn, dr, cr]);
    });

    const columnWidths = {
      0: { cellWidth: 20 }, // Date
      1: { cellWidth: 72 }, // Particular
      2: { cellWidth: 34 }, // Voucher Type
      3: { cellWidth: 12 }, // Voucher No
      4: { cellWidth: 26 }, // Debit
      5: { cellWidth: 26 }, // Credit
    };

    autoTable(doc, {
      startY: y + 14,
      head: [
        ["Date", "Particular", "Voucher Type", "Voucher No", "Debit", "Credit"],
      ],
      body,
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133], halign: "center" },
      styles: {
        fontSize: 9,
        cellPadding: 1.8,
        overflow: "linebreak",
      },
      margin: { left: 10, right: 10 },
      tableWidth: "auto",
      columnStyles: columnWidths,
    });

    const { ttlDebit, ttlCredit, closingDiff, showOnDebit, showOnCredit } =
      computeSummary(ledger);

    const finalY =
      doc.lastAutoTable && doc.lastAutoTable.finalY
        ? Number(doc.lastAutoTable.finalY) + 4
        : y + 20;

    autoTable(doc, {
      startY: finalY,
      body: [
        [
          "",
          "Closing Balance",
          "",
          "",
          showOnDebit ? closingDiff.toFixed(2) : "",
          showOnCredit ? closingDiff.toFixed(2) : "",
        ],
        [
          "",
          "Total",
          "",
          "",
          Math.max(ttlDebit, ttlCredit).toFixed(2),
          Math.max(ttlDebit, ttlCredit).toFixed(2),
        ],
      ],
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 1.8 },
      margin: { left: 10, right: 10 },
      tableWidth: "auto",
      columnStyles: columnWidths,
    });

    return doc;
  };

  // Preview (PDF icon)
  const previewPDF = async () => {
    if (!ledger.length) return;

    const strapLogoDataUrl = await loadImageAsDataUrl(RupioSoftLogo);

    const logoFile = companyDetail?.logo || "";
    const logoSrc = logoFile ? imgURL(logoFile) : "";
    const mainLogoDataUrl = logoSrc ? await loadImageAsDataUrl(logoSrc) : null;

    const doc = buildLedgerPdf({ strapLogoDataUrl, mainLogoDataUrl });
    const url = doc.output("bloburl");
    setPdfUrl(url);
    setPdfOpen(true);
  };

  // XLSX (Download icon)
  const downloadExcel = () => {
    if (!ledger.length) return;

    const rows = [];
    rows.push([""]);
    if (companyDetail?.name) rows.push([toStr(companyDetail.name)]);
    if (companyDetail?.address) rows.push([toStr(companyDetail.address)]);
    if (companyDetail?.email) rows.push([toStr(companyDetail.email)]);
    if (companyDetail?.mobileNo) rows.push([toStr(companyDetail.mobileNo)]);
    rows.push([
      `${partyName || ""} - Ledger${selectedFY ? ` (${selectedFY})` : ""}`,
    ]);
    rows.push([]);

    rows.push([
      "Date",
      "Particular",
      "Voucher Type",
      "Voucher No",
      "Debit",
      "Credit",
    ]);

    const obDr =
      selectedYearOpening.openingType === "debit"
        ? safeNum(selectedYearOpening.openingBalance).toFixed(2)
        : "";
    const obCr =
      selectedYearOpening.openingType === "credit"
        ? safeNum(selectedYearOpening.openingBalance).toFixed(2)
        : "";
    rows.push(["", "Opening Balance", "", "", obDr, obCr]);

    ledger.forEach((x) => {
      rows.push([
        fmtDate(x?.date),
        getLedgerParticular(x, partyName),
        toStr(x?.voucherType),
        toStr(x?.invoiceId || x?.voucherNo || ""),
        x?.debit ? safeNum(x.debit).toFixed(2) : "",
        x?.credit ? safeNum(x.credit).toFixed(2) : "",
      ]);
    });

    const { ttlDebit, ttlCredit, closingDiff, showOnDebit, showOnCredit } =
      computeSummary(ledger);

    rows.push([
      "",
      "Closing Balance",
      "",
      "",
      showOnDebit ? closingDiff.toFixed(2) : "",
      showOnCredit ? closingDiff.toFixed(2) : "",
    ]);

    rows.push([
      "",
      "Total",
      "",
      "",
      Math.max(ttlDebit, ttlCredit).toFixed(2),
      Math.max(ttlDebit, ttlCredit).toFixed(2),
    ]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ledger");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const filename = `${(partyName || "Party").replace(
      /[^a-zA-Z0-9_-]/g,
      "_",
    )}-Ledger${selectedFY ? `-${selectedFY}` : ""}.xlsx`;
    saveAs(new Blob([buf], { type: "application/octet-stream" }), filename);
  };

  // per-row invoice PDF download (click invoice no)
  const downloadInvoicePDF = async (row) => {
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const db = user?.database;
      if (!row?.invoiceId) return;

      const response = await _GetById(
        View_Invoice_Data_by_id,
        db,
        row.invoiceId,
      );
      const printData = response?.printData;
      if (!printData) {
        swal("Invoice data not found");
        return;
      }

      const details = Array.isArray(printData?.orderItems)
        ? printData.orderItems
        : [];
      const AllCharges = Array.isArray(printData?.Applied_Charges)
        ? printData.Applied_Charges
        : [];

      const enriched = {
        ...printData,
        gstDetails: HsnSummaryCalculation(printData),
      };

      try {
        const res = await _Get(Last_Ledger_Balance, enriched?.partyId?._id);
        enriched.lastLedgerBalance =
          res?.Ledger?.debit || res?.Ledger?.credit || "Not Found";
      } catch {
        enriched.lastLedgerBalance = "Not Found";
      }

      const blob = await pdf(
        <LastestInvoice
          invoiceData={enriched}
          CollectedData={{}}
          BilData={{
            CompanyDetails: companyDetail || {},
            BillNumber: enriched.invoiceId,
            PrintData: enriched,
            Applied_Charges: AllCharges,
            AllbillMerged: details,
            wordsNumber: "",
            deliveryCharges: safeNum(enriched?.deliveryCharges),
            otherCharges: safeNum(enriched?.otherCharges),
            discount: safeNum(enriched?.discount),
            qr: "",
            upiid: "",
            totalTaxable: 0,
            ledgerBalance: 0,
          }}
          tableList={details}
          AllCharges={AllCharges}
          fileName="invoice.pdf"
        />,
      ).toBlob();

      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      const safeParty = (
        enriched?.partyId?.CompanyName ||
        enriched?.fullName ||
        "Invoice"
      ).replace(/[^a-zA-Z0-9_-]/g, "_");

      a.href = url;
      a.download = `${safeParty}-${enriched?.invoiceId || row.invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      swal("Failed to download invoice PDF");
    }
  };

  // ---- UI ----
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary" />
      </div>
    );
  }

  const noRows = !ledger.length;

  return (
    <>
      {/* === Toolbar Card (like WareHouseList) === */}
      <Row className="wh-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="pl-card"
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <CardBody>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "flex-end",
                }}
              >
                <div
                  style={{
                    fontWeight: 800,
                    textTransform: "uppercase",
                    fontSize: 18,
                  }}
                >
                  Party Ledger{" "}
                  <span className="count-pill" title="records">
                    {ledger.length}
                  </span>
                </div>

                <div style={{ flex: "1 1 240px" }} />

                {/* SuperAdmin Switch */}
                {isMaster && (
                  <div>
                    <SuperAdminUI
                      onDropdownChange={(val) =>
                        localStorage.setItem(
                          "SuperadminIdByMaster",
                          JSON.stringify(val),
                        )
                      }
                      onSubmit={handleParentSubmit}
                    />
                  </div>
                )}

                {/* Party Picker (auto-load ledger on select) */}
                <div style={{ minWidth: 260 }}>
                  <Label className="m-0">Party</Label>
                  <Multiselect
                    selectionLimit={1}
                    isObject={true}
                    options={partyList}
                    selectedValues={selectedParty}
                    onSelect={handleSelectionParty}
                    onRemove={onRemoveParty}
                    displayValue="fullName"
                  />
                </div>

                {/* Financial Year Picker */}
                {/* {partyId && financialYears.length > 0 && (
                  <div style={{ minWidth: 140 }}>
                    <Label className="m-0">Financial Year</Label>
                    <Input
                      type="select"
                      value={selectedFY || ""}
                      onChange={handleFinancialYearChange}
                    >
                      {financialYears.map((fy) => (
                        <option key={fy} value={fy}>
                          {fy}
                        </option>
                      ))}
                    </Input>
                  </div>
                )} */}

                {/* Date Filters + Apply */}
                <div>
                  <Label className="m-0">Start Date</Label>
                  <Input
                    type="date"
                    value={filter.startDate || ""}
                    onChange={(e) =>
                      setFilter((p) => ({ ...p, startDate: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <Label className="m-0">End Date</Label>
                  <Input
                    type="date"
                    value={filter.EndDate || ""}
                    onChange={(e) =>
                      setFilter((p) => ({ ...p, EndDate: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <Button
                    color="primary"
                    onClick={applyDateFilter}
                    style={{ height: 38 }}
                    disabled={busy || !selectedFY}
                  >
                    {busy ? <Spinner size="sm" /> : "Apply"}
                  </Button>
                </div>

                {/* Actions: PDF Preview (icon) + XLSX (icon) */}
                <div className="d-flex" style={{ gap: 10 }}>
                  <AiOutlineFilePdf
                    size={28}
                    title="Preview PDF"
                    style={{
                      cursor: noRows ? "not-allowed" : "pointer",
                      opacity: noRows ? 0.5 : 1,
                    }}
                    onClick={() => !noRows && previewPDF()}
                    className="no-print"
                    color="rgb(8, 91, 245)"
                  />
                  <ImDownload
                    size={24}
                    title="Download .XLSX"
                    style={{
                      cursor: noRows ? "not-allowed" : "pointer",
                      opacity: noRows ? 0.5 : 1,
                      marginTop: 6,
                    }}
                    onClick={() => !noRows && downloadExcel()}
                    className="no-print"
                    color="rgb(8, 91, 245)"
                  />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* === Table Card === */}
      <Row>
        <Col sm="12">
          <Card
            className="pl-card"
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {!partyId ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                Select a party to view the ledger.
              </div>
            ) : financialYears.length === 0 ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No ledger entries found for this party.
              </div>
            ) : (
              <div className="wh-table-wrap">
                <table className="wh-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Particular</th>
                      <th>Voucher Type</th>
                      <th>Voucher No</th>
                      <th>Debit</th>
                      <th>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Opening Balance */}
                    <tr>
                      <td />
                      <td style={{ fontWeight: 700 }}>
                        Opening Balance {selectedFY ? `(${selectedFY})` : ""}
                      </td>
                      <td />
                      <td />
                      <td style={{ fontWeight: 700 }}>
                        {selectedYearOpening.openingType === "debit"
                          ? safeNum(selectedYearOpening.openingBalance).toFixed(
                              2,
                            )
                          : 0}
                      </td>
                      <td style={{ fontWeight: 700 }}>
                        {selectedYearOpening.openingType === "credit"
                          ? safeNum(selectedYearOpening.openingBalance).toFixed(
                              2,
                            )
                          : 0}
                      </td>
                    </tr>

                    {/* Ledger entries */}
                    {ledger.map((ele, i) => {
                      const company = companyDetail || {};
                      const formattedInvoice = ele?.invoiceId
                        ? `${toStr(company.Prefix || "")}${ele.invoiceId}${toStr(
                            company.Suffix || "",
                          )}`
                        : ele?.voucherNo || "-";
                      const hasInvoice = !!ele?.invoiceId;

                      return (
                        <tr key={ele?._id || i}>
                          <td>{fmtDate(ele?.date)}</td>
                          <td>{getLedgerParticular(ele, partyName)}</td>
                          <td>{ele?.voucherType || "-"}</td>
                          <td>
                            {hasInvoice ? (
                              <span
                                onClick={() => downloadInvoicePDF(ele)}
                                style={{
                                  color: "rgb(8,91,245)",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                  fontWeight: 600,
                                }}
                                title="Download Invoice PDF"
                              >
                                {formattedInvoice}
                              </span>
                            ) : (
                              formattedInvoice
                            )}
                          </td>
                          <td>
                            {ele?.debit ? safeNum(ele.debit).toFixed(2) : ""}
                          </td>
                          <td>
                            {ele?.credit ? safeNum(ele.credit).toFixed(2) : ""}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Closing & Totals */}
                    {(() => {
                      const {
                        ttlDebit,
                        ttlCredit,
                        closingDiff,
                        showOnDebit,
                        showOnCredit,
                      } = computeSummary(ledger);

                      return (
                        <>
                          <tr>
                            <td colSpan={4} style={{ fontWeight: 700 }}>
                              Closing Balance
                            </td>
                            <td style={{ fontWeight: 700 }}>
                              {showOnDebit ? closingDiff.toFixed(2) : ""}
                            </td>
                            <td style={{ fontWeight: 700 }}>
                              {showOnCredit ? closingDiff.toFixed(2) : ""}
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={4} style={{ fontWeight: 700 }}>
                              Total
                            </td>
                            <td style={{ fontWeight: 700 }}>
                              {Math.max(ttlDebit, ttlCredit).toFixed(2)}
                            </td>
                            <td style={{ fontWeight: 700 }}>
                              {Math.max(ttlDebit, ttlCredit).toFixed(2)}
                            </td>
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* PDF Preview Modal */}
      <Modal
        isOpen={pdfOpen}
        toggle={() => setPdfOpen(false)}
        size="xl"
        className="no-print"
      >
        <ModalHeader toggle={() => setPdfOpen(false)}>
          {partyName || "Party"} — Ledger {selectedFY ? `(${selectedFY})` : ""}{" "}
          (Preview)
        </ModalHeader>
        <ModalBody style={{ height: "80vh" }}>
          {pdfUrl ? (
            <iframe
              title="Ledger PDF"
              src={pdfUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "100%" }}
            >
              <Spinner />
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}
